/**
 * CompletionBar — guest-facing engagement tracker.
 *
 * Shows ONLY after the guest has had at least one interaction (either
 * a NEW pip cleared via tab-tap or a completed feature). Hides itself
 * once everything is done. Collapses to a mini badge after 60s of
 * dwell + ≥2 completed (the guest has demonstrably engaged — no need
 * to keep nudging).
 *
 * Receives `slug` + `features[]` from the parent; reads localStorage
 * directly via lib/features helpers so the component is portable
 * across GuestLanding + GuestHome with zero prop drilling.
 */
import React, { useEffect, useState } from 'react';
import { CaretRight, Sparkle } from '@phosphor-icons/react';
import {
  completionStats,
  COMPLETION_EMOJI,
  FEATURE_CARDS,
  FEATURE_TAB_MAP,
} from '../lib/features';

const NEXT_LABEL = {
  upload:           'Upload your photos',
  memory_guestbook: 'Leave a message',
  video_guestbook:  'Record a video',
  pocket_booth:     'Snap a Pocket Booth',
  challenge_mode:   'Play Challenges',
};

export default function CompletionBar({ slug, features, onTabHint }) {
  // Bumped on every visibilitychange / storage event / completion-tick
  // so the bar reactively reflects writes from sibling pages (e.g. the
  // memory-book submit page writes localStorage, then back-navigates
  // to the guest landing).
  const [tick, setTick] = useState(0);
  const [mountedAt] = useState(() => Date.now());

  useEffect(() => {
    const onStorage = () => setTick((t) => t + 1);
    const onVis = () => { if (document.visibilityState === 'visible') setTick((t) => t + 1); };
    const onComplete = () => setTick((t) => t + 1);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('eventdrop:completion_tick', onComplete);
    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('eventdrop:completion_tick', onComplete);
    };
  }, []);

  const stats = completionStats(slug, features);
  // Read tick to keep React happy about the dependency
  void tick;

  // Don't show when there's nothing to track or nothing to encourage.
  if (stats.total <= 1) return null;
  // Don't show before any interaction — bar should appear AS the guest
  // earns it, not as a static "0 of 5" prompt.
  if (stats.completed === 0) return null;
  // All done — celebrate by hiding.
  if (stats.completed >= stats.total) return null;

  const percent = Math.round((stats.completed / stats.total) * 100);
  const dwellMs = Date.now() - mountedAt;
  const collapsed = stats.completed >= 2 && dwellMs > 60_000;
  const almostThere = stats.completed === stats.total - 1;
  const next = stats.nextFeature;
  const nextEmoji = next ? COMPLETION_EMOJI[next] : '';
  const nextLabel = next ? (NEXT_LABEL[next] || (FEATURE_CARDS[next]?.title || 'Try the next experience')) : '';
  const nextTab = next ? FEATURE_TAB_MAP[next] : null;

  if (collapsed) {
    return (
      <button
        onClick={() => nextTab && onTabHint?.(nextTab)}
        className="max-w-md mx-auto mb-4 w-full flex items-center justify-center gap-1.5 text-[11px] font-black tracking-[1.5px] uppercase text-[#C29D52]/85/80 hover:text-[#C29D52]/85 transition-colors"
        data-testid="completion-bar-mini"
      >
        <Sparkle size={11} weight="fill" />
        {stats.completed}/{stats.total} completed
      </button>
    );
  }

  return (
    <div className="max-w-md mx-auto mb-4 rounded-2xl p-4 border border-white/10" style={{ background: 'linear-gradient(135deg, rgba(194,157,82,0.08), rgba(184,151,98,0.04))' }} data-testid="completion-bar">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-black tracking-[1.5px] uppercase text-[#C29D52]/85" data-testid="completion-bar-status">
          {almostThere
            ? <>🎉 One more to go</>
            : <>✨ {stats.completed} of {stats.total} unlocked</>}
        </p>
        <p className="text-[10px] font-black text-white/40">{percent}%</p>
      </div>
      <div className="w-full h-1.5 rounded-full bg-white/[0.06] overflow-hidden mb-3" data-testid="completion-bar-track">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percent}%`, background: 'linear-gradient(90deg, #C29D52, #B89762)' }}
          data-testid="completion-bar-fill"
        />
      </div>
      {next && (
        <button
          onClick={() => nextTab && onTabHint?.(nextTab)}
          className="w-full flex items-center justify-between text-left text-xs text-white/70 hover:text-white transition-colors group"
          data-testid="completion-bar-next"
        >
          <span className="truncate">
            <span className="text-white/40 mr-1">Next:</span>
            <span className="font-black">{nextEmoji} {nextLabel}</span>
          </span>
          <CaretRight size={11} weight="bold" className="text-white/40 group-hover:text-white/80 flex-shrink-0" />
        </button>
      )}
    </div>
  );
}
